import { Link } from 'react-router-dom';
import { Instagram, Facebook, Twitter, Linkedin, MessageCircle, MapPin } from 'lucide-react';
import SafeEmail from '../ui/SafeEmail';

export default function Footer() {
  return (
    <footer className="border-t border-white/5 bg-black pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-12 mb-16">
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="mb-4 inline-flex items-center group">
              <img 
                src="/logo.webp" 
                alt="HashQ Technologies" 
                width={174}
                height={40}
                className="h-8 md:h-10 w-auto invert brightness-0"
                decoding="async"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              <div className="hidden text-2xl font-bold tracking-tighter items-center gap-0">
                <span className="text-white">Hashq</span>
                <span className="text-blue-500">.</span>
              </div>
            </Link>
            <p className="text-gray-400 max-w-sm mt-4 leading-relaxed">
              Technology should empower, not complicate. We build intelligent software that simplifies decision-making for businesses.
            </p>
            <div className="mt-8 space-y-2 font-mono text-sm">
              <SafeEmail className="flex items-center gap-2 text-[#CCFF00] hover:text-white transition-colors">
                <span className="text-gray-400">Email:</span> crew@hashq.in
              </SafeEmail>
              <a href="tel:+917012607911" className="flex items-center gap-2 text-[#06B6D4] hover:text-white transition-colors">
                <span className="text-gray-400">Phone:</span> +91-7012607911
              </a>
              <div className="flex items-start gap-2 text-gray-400 pt-1">
                <MapPin size={14} className="mt-0.5 flex-shrink-0 text-[#8B5CF6]" />
                <span className="text-[11px] leading-relaxed">
                  16/193/C, First Floor, Panthayil Building,<br />
                  PO, Kuttoor, Thrissur,<br />
                  Kerala 680013, India
                </span>
              </div>
            </div>
          </div>
          
          <div>
            <p className="text-white font-semibold mb-6 uppercase tracking-wider text-sm">Services</p>
            <ul className="space-y-4">
              <li><Link to="/services/nodejs-development" className="text-gray-400 hover:text-white transition-colors">Node.js Backend</Link></li>
              <li><Link to="/services/flutter-development" className="text-gray-400 hover:text-white transition-colors">Flutter Apps</Link></li>
              <li><Link to="/services/laravel-development" className="text-gray-400 hover:text-white transition-colors">Laravel Systems</Link></li>
              <li><Link to="/services/react-development" className="text-gray-400 hover:text-white transition-colors">React Frontends</Link></li>
              <li><Link to="/services/nextjs-development" className="text-gray-400 hover:text-white transition-colors">Next.js & SSR</Link></li>
              <li><Link to="/services/postgresql-database-architecture" className="text-gray-400 hover:text-white transition-colors">PostgreSQL Architecture</Link></li>
              <li><Link to="/services/aws-cloud-infrastructure" className="text-gray-400 hover:text-white transition-colors">AWS Cloud Infrastructure</Link></li>
              <li><Link to="/services/startup-company-kerala" className="text-gray-400 hover:text-white transition-colors">Tech Startup Kerala</Link></li>
            </ul>
          </div>


          <div>
            <p className="text-white font-semibold mb-6 uppercase tracking-wider text-sm">Company</p>
            <ul className="space-y-4">
              <li><Link to="/portfolio" className="text-gray-400 hover:text-white transition-colors">Portfolio</Link></li>
              <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors">Blog</Link></li>
              <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div>
            <p className="text-white font-semibold mb-6 uppercase tracking-wider text-sm">Verified</p>
            <ul className="space-y-4 font-mono text-sm leading-none">
               <li>
                <a href="https://clutch.co/profile/hashq-technologies" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-[#CCFF00] transition-colors group">
                  <span className="group-hover:translate-x-1 transition-transform inline-block">→</span> [ CLUTCH ]
                </a>
              </li>
              <li>
                <a href="https://www.upwork.com/freelancers/~013bb1141aea2af331" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-[#CCFF00] transition-colors group">
                  <span className="group-hover:translate-x-1 transition-transform inline-block">→</span> [ UPWORK ]
                </a>
              </li>
              <li>
                <a href="https://www.fiverr.com/hashqtechnology" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-[#CCFF00] transition-colors group">
                  <span className="group-hover:translate-x-1 transition-transform inline-block">→</span> [ FIVERR ]
                </a>
              </li>
            </ul>
          </div>

          <div>
            <p className="text-white font-semibold mb-6 uppercase tracking-wider text-sm">Socials</p>
            <ul className="space-y-4 font-mono text-sm">
              <li>
                <a href="https://www.instagram.com/hashqtechnologies" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-[#CCFF00] transition-colors focus:text-[#CCFF00] group">
                  <Instagram size={16} className="group-hover:scale-110 transition-transform" /> <span>[ INSTAGRAM ]</span>
                </a>
              </li>
              <li>
                <a href="https://www.facebook.com/hashqtech" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-[#06B6D4] transition-colors focus:text-[#06B6D4] group">
                  <Facebook size={16} className="group-hover:scale-110 transition-transform" /> <span>[ FACEBOOK ]</span>
                </a>
              </li>
              <li>
                <a href="https://x.com/hashqtech" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors focus:text-white group">
                  <Twitter size={16} className="group-hover:scale-110 transition-transform" /> <span>[ TWITTER ]</span>
                </a>
              </li>
              <li>
                <a href="https://www.linkedin.com/company/hashqtechnologies/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-[#06B6D4] transition-colors focus:text-[#06B6D4] group">
                  <Linkedin size={16} className="group-hover:scale-110 transition-transform" /> <span>[ LINKEDIN ]</span>
                </a>
              </li>
              <li>
                <a href="https://wa.me/917012607911" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-400 hover:text-[#25D366] transition-colors focus:text-[#25D366] group">
                  <MessageCircle size={16} className="group-hover:scale-110 transition-transform" /> <span>[ WHATSAPP ]</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} HashQ Technologies. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="text-gray-500 hover:text-white transition-colors text-sm">Privacy Policy</Link>
            <Link to="/terms-of-service" className="text-gray-500 hover:text-white transition-colors text-sm">Terms of Service</Link>
            <a href="/.well-known/security.txt" className="text-gray-500 hover:text-white transition-colors text-sm">Security</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
